import { Image } from 'expo-image';
import { useMemo, useState } from 'react';
import { ImageStyle, StyleProp, TouchableWithoutFeedback, View, ViewStyle } from 'react-native';

import { Button, IconCheck, IconOffer, Text, createMqStyles, tokens } from '@fhs/ui';

export type OfferCardProps = {
  id: string;
  imageUrl: string;
  /** CMS / API alt when available; folded into the image control label (image stays decorative). */
  imageAlt?: string | null;
  title: string;
  description: string;
  /** Short, formatted expiry label (e.g. "Expires in 6 days"). Hidden when undefined. */
  expiry?: string;
  onPressButton: () => Promise<void> | void;
  onPressImage: () => void;
  onPressTitle: () => void;
  isDisabled?: boolean;
  buttonLabel: string;
  buttonVariant?: 'default' | 'applied';
  style?: StyleProp<ViewStyle>;
  tag?: string | null;
  isHidden?: boolean;
};

export function OfferCard({
  imageUrl,
  imageAlt,
  title,
  description,
  expiry,
  onPressButton,
  onPressImage,
  onPressTitle,
  buttonLabel,
  isDisabled,
  style,
  tag,
  isHidden,
  buttonVariant = 'default',
}: OfferCardProps) {
  const isApplied = buttonVariant === 'applied';
  const [actionIsPending, setActionIsPending] = useState(false);
  const styles = useMqStyles();

  const isApplyingOrApplied = actionIsPending || isApplied;
  const imageControlAccessibilityLabel = useMemo(() => {
    const alt = imageAlt?.trim();
    return alt ? `View offer details, ${title}. ${alt}` : `View offer details, ${title}`;
  }, [imageAlt, title]);
  const titleAccessibilityLabel = useMemo(
    () => [title, description, expiry].filter(Boolean).join('. '),
    [title, description, expiry]
  );
  const applyingAndAppliedStyle = {
    backgroundColor: tokens.colors.$success,
    borderColor: tokens.colors.$success,
  };

  if (isHidden) {
    return <View style={style} />;
  }
  return (
    <View style={[styles.container, style]}>
      <TouchableWithoutFeedback
        accessibilityHint="Opens full offer details"
        accessibilityLabel={imageControlAccessibilityLabel}
        accessibilityRole="button"
        onPress={onPressImage}
      >
        <View style={styles.imageContainer}>
          <Image
            accessibilityElementsHidden
            accessible={false}
            alt=""
            importantForAccessibility="no-hide-descendants"
            style={styles.image as StyleProp<ImageStyle>}
            source={{ uri: imageUrl }}
            contentFit="contain"
          />
          {tag && (
            <View style={styles.tagBadge}>
              <View
                accessible={false}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              >
                <IconOffer size={16} color={tokens.colors.$white} />
              </View>
              <Text.Ui size="xs" weight="semibold" style={styles.tagBadgeText}>
                {tag}
              </Text.Ui>
            </View>
          )}
        </View>
      </TouchableWithoutFeedback>
      <View style={styles.contentContainer}>
        <TouchableWithoutFeedback
          accessibilityHint="Opens offer details"
          accessibilityLabel={titleAccessibilityLabel}
          accessibilityRole="button"
          onPress={onPressTitle}
        >
          <View style={styles.offerTextBlock}>
            <Text.Ui
              accessible={false}
              size="md"
              weight="semibold"
              ellipsizeMode="tail"
              numberOfLines={2}
            >
              {title}
            </Text.Ui>
            <Text.Ui accessible={false} size="sm" numberOfLines={2} ellipsizeMode="tail">
              {description}
            </Text.Ui>
          </View>
        </TouchableWithoutFeedback>
        {expiry && (
          <Text.Ui accessible={false} size="sm" style={styles.expiry} numberOfLines={1}>
            {expiry}
          </Text.Ui>
        )}
        <Button
          accessibilityLabel={
            actionIsPending ? `Applying, ${title}` : `${buttonLabel}, ${title}`
          }
          key={`${isApplyingOrApplied}`}
          style={[styles.buttonContainer, isApplyingOrApplied && applyingAndAppliedStyle]}
          hoveredStyle={isApplyingOrApplied && applyingAndAppliedStyle}
          disabledStyle={isApplyingOrApplied && { backgroundColor: tokens.colors.$success }}
          disabled={isDisabled || actionIsPending}
          loading={actionIsPending}
          type={isApplyingOrApplied ? 'solid' : 'outline'}
          size="xs"
          onPress={async () => {
            setActionIsPending(true);
            try {
              await onPressButton();
            } finally {
              setActionIsPending(false);
            }
          }}
        >
          {isApplied && (
            <Button.Icon color={tokens.colors.$white} size={16}>
              <IconCheck />
            </Button.Icon>
          )}
          <Button.Text
            style={[styles.buttonLabel, isApplyingOrApplied && { color: tokens.colors.$white }]}
          >
            {actionIsPending ? 'Applying' : buttonLabel}
          </Button.Text>
        </Button>
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      width: 215,
      height: 330,
      borderColor: tokens.colors.$blackOpacity10,
      borderWidth: 1,
      borderRadius: 8,
      alignItems: 'center',
      overflow: 'hidden',
    },
    $ltDesktop: {
      width: 160,
      height: 280,
    },
  },
  contentContainer: {
    $base: {
      flex: 1,
      paddingTop: 0,
      padding: 16,
      width: '100%',
    },
    $ltDesktop: {
      padding: 12,
    },
  },
  offerTextBlock: {
    $base: {
      flex: 1,
      paddingTop: 16,
      paddingLeft: 16,
      paddingRight: 16,
      marginLeft: -16,
      marginRight: -16,
      cursor: 'pointer',
      gap: 8,
    },
    $ltDesktop: {
      paddingTop: 12,
      paddingLeft: 12,
      paddingRight: 12,
      marginLeft: -12,
      marginRight: -12,
    },
  },
  expiry: {
    $base: {
      color: tokens.colors.$blackOpacity55,
    },
  },
  imageContainer: {
    $base: {
      width: '100%',
      borderTopStartRadius: 8,
      borderTopEndRadius: 8,
      backgroundColor: tokens.colors.$white,
      borderBottomWidth: 1,
      borderBottomColor: tokens.colors.$black10,
      overflow: 'hidden',
      cursor: 'pointer',
    },
  },
  image: {
    $base: {
      width: '100%',
      aspectRatio: 5 / 3,
    },
  },
  infoIcon: {
    $base: {
      position: 'absolute',
      right: 12,
      top: 12,
    },
    $ltDesktop: {
      right: 8,
      top: 8,
    },
  },

  buttonContainer: {
    $base: {
      marginTop: 16,
      height: 34,
    },
    $ltDesktop: {
      height: 31,
    },
  },
  buttonLabel: {
    $ltDesktop: {
      fontSize: 10,
      lineHeight: 14,
    },
  },
  tagBadge: {
    $base: {
      bottom: 8,
      flexDirection: 'row',
      right: 8,
      color: tokens.colors.$white,
      backgroundColor: tokens.colors.$black,
      paddingVertical: 4,
      paddingHorizontal: 8,
      position: 'absolute',
      borderRadius: 4,
      gap: 4,
    },
  },
  tagBadgeText: {
    $base: {
      color: tokens.colors.$white,
    },
  },
});
